import React from 'react';
import clsx from 'clsx';
import { useReducedMotion } from 'framer-motion';

export type ProviderLogoItem = {
  name: string;
  alt?: string;
  logo?: React.ReactNode;
  src?: string;
  srcs?: string[];
  href?: string;
  className?: string;
};

export type LLMProvidersMarqueeProps = {
  items: ProviderLogoItem[];
  speedSeconds?: number;
  hoverPause?: boolean;
  grayscale?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  heightPx?: number;
  itemGapPx?: number;
  maxLogoWidthPx?: number;
  className?: string;
  containerClassName?: string;
  showTopStripe?: boolean;
  barPaddingY?: number;
  edgeFadePercent?: number;
};

const sizeToHeight = (s: string): number => {
  switch (s) {
    case 'sm': return 16;
    case 'md': return 22;
    case 'lg': return 28;
    case 'xl': return 36; // Reduced from 44
    case '2xl': return 48; // Reduced from 68
    default: return 22;
  }
};

const LogoImg: React.FC<{
  sources: string[];
  alt: string;
  height: number;
  maxWidth: number;
}> = ({ sources, alt, height, maxWidth }) => {
  const [idx, setIdx] = React.useState(0);
  const src = sources[idx];
  if (!src) return null;
  return (
    <img
      key={src}
      src={src}
      alt={alt}
      className="object-contain block"
      loading="lazy"
      height={height}
      style={{ height, maxWidth }}
      onError={() => setIdx((i) => (i + 1 < sources.length ? i + 1 : i))}
    />
  );
};

export const LLMProvidersMarquee: React.FC<LLMProvidersMarqueeProps> = ({
  items,
  speedSeconds = 24,
  hoverPause = true,
  grayscale = true,
  size = 'xl',
  heightPx,
  itemGapPx = 80, // Reduced gap
  maxLogoWidthPx = 200,
  className,
  containerClassName,
  showTopStripe = false,
  barPaddingY = 24, // Reduced padding
  edgeFadePercent = 12,
}) => {
  const reduce = useReducedMotion();
  if (!items || items.length === 0) return null;

  const track = React.useMemo(() => [...items, ...items, ...items], [items]);
  const logoH = heightPx ?? sizeToHeight(size);

  return (
    <section className={clsx('bg-slate-50', className)}>
      <div
        className={clsx('max-w-7xl mx-auto px-4 md:px-6 lg:px-8', containerClassName)}
        style={{ paddingTop: barPaddingY, paddingBottom: barPaddingY }}
      >
        <div
          className={clsx(
            'relative border-2 border-black rounded-2xl bg-white overflow-hidden group',
            hoverPause && 'group'
          )}
        >
          {showTopStripe && (
            <div
              aria-hidden="true"
              className="absolute inset-x-2 top-2 h-2 rounded-full bg-slate-300"
            />
          )}

          <div className="relative">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-0 top-0 h-full z-10"
              style={{
                width: `${edgeFadePercent}%`,
                backgroundImage: 'linear-gradient(to right, white, rgba(255,255,255,0))',
              }}
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute right-0 top-0 h-full z-10"
              style={{
                width: `${edgeFadePercent}%`,
                backgroundImage: 'linear-gradient(to left, white, rgba(255,255,255,0))',
              }}
            />

            {/* UPDATED: Tighter height constraints */}
            <div
              className={clsx(
                'relative h-[70px] md:h-[90px] flex items-center', // Reduced height significantly
                'overflow-hidden'
              )}
            >
              <div
                className={clsx(
                  'absolute inset-0 flex items-center w-auto min-w-max gap-0 will-change-transform',
                  grayscale && 'grayscale hover:grayscale-0 transition-[filter] duration-200',
                  reduce ? '' : 'animate-[llm-marquee_var(--dur)_linear_infinite]',
                  hoverPause && !reduce && 'group-hover:[animation-play-state:paused]'
                )}
                style={reduce ? undefined : ({ ['--dur' as any]: `${Math.max(6, speedSeconds)}s` } as React.CSSProperties)}
              >
                <ul className="flex items-center min-w-max gap-0">
                  {track.map((item, i) => {
                    const sources = item.srcs ?? (item.src ? [item.src] : []);
                    const alt = item.alt ?? item.name;

                    return (
                      <li key={`${item.name}-${i}`} className="shrink-0 flex items-center justify-center">
                        <div style={{ marginRight: itemGapPx }} className="flex items-center justify-center">
                            {item.logo ? (
                            <span
                                className="flex items-center justify-center shrink-0"
                                style={{ height: logoH, maxWidth: maxLogoWidthPx }}
                            >
                                {item.logo}
                            </span>
                            ) : sources.length > 0 ? (
                            <LogoImg
                                sources={sources}
                                alt={alt}
                                height={logoH}
                                maxWidth={maxLogoWidthPx}
                            />
                            ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {!reduce && (
                <style>
                  {`
                  @keyframes llm-marquee {
                    0% { transform: translate3d(0,0,0); }
                    100% { transform: translate3d(-33.3333%, 0, 0); }
                  }
                  `}
                </style>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LLMProvidersMarquee;